import { useEffect, useRef, useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { RotateCcw, RotateCw, Compass, MapPin, Camera, Satellite, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PropertyImage from "./PropertyImage";
import api, { formatApiError } from "../lib/api";

const COMPASS = [
  { label: "N", value: 0 },
  { label: "E", value: 90 },
  { label: "S", value: 180 },
  { label: "W", value: 270 },
];

// Aspect ratio of the property card hero image (matches 640x320 / 600x300).
const CROP_ASPECT = 2; // width / height

/**
 * Property Photo Editor — three modes:
 *   • Street View  (heading / auto)
 *   • Aerial       (Maps Static)
 *   • Custom upload (file → crop/zoom → JPEG)
 *
 * Custom uploads short-circuit Google entirely on the backend.
 */
export function RetakePhotoDialog({ property, open, onClose, onSaved }) {
  // mode: "streetview" | "staticmap" | "upload"
  const [mode, setMode] = useState("streetview");
  const [heading, setHeading] = useState(0);
  const [useAuto, setUseAuto] = useState(true);
  const [saving, setSaving] = useState(false);

  // Upload state
  const [uploadSrc, setUploadSrc] = useState(null);   // dataURL of user's chosen file
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);

  const onCropComplete = useCallback((_area, areaPx) => {
    setCroppedAreaPixels(areaPx);
  }, []);

  // Re-seed every time a new property opens
  useEffect(() => {
    if (!property) return;
    const hasStored = property.image_heading !== undefined && property.image_heading !== null;
    setHeading(hasStored ? Number(property.image_heading) : 0);
    setUseAuto(!hasStored);
    if (property.custom_image_data_uri || property.has_custom_image) {
      setMode("upload");
    } else {
      setMode(property.image_source === "staticmap" ? "staticmap" : "streetview");
    }
    setUploadSrc(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  }, [property?.id, open]);

  if (!property) return null;

  const nudge = (delta) => {
    setUseAuto(false);
    setHeading((h) => ((Number(h) + delta) % 360 + 360) % 360);
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error("Please choose a PNG, JPG, or WEBP image.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Image is too large (max 12 MB before cropping).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setUploadSrc(reader.result);
    reader.readAsDataURL(f);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const buildCroppedBlob = async () => {
    if (!uploadSrc || !croppedAreaPixels) return null;
    const img = await new Promise((resolve, reject) => {
      const i = new window.Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = uploadSrc;
    });
    const canvas = document.createElement("canvas");
    // Cap output at 1600x800 for a reasonable file size.
    const maxW = 1600;
    const outW = Math.min(maxW, croppedAreaPixels.width);
    const outH = Math.round(outW / CROP_ASPECT);
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      img,
      croppedAreaPixels.x, croppedAreaPixels.y,
      croppedAreaPixels.width, croppedAreaPixels.height,
      0, 0, outW, outH,
    );
    return await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
  };

  const saveCustomUpload = async () => {
    if (!uploadSrc || !croppedAreaPixels) {
      toast.error("Choose an image first");
      return;
    }
    setSaving(true);
    try {
      const blob = await buildCroppedBlob();
      if (!blob) throw new Error("Could not crop image");
      const fd = new FormData();
      fd.append("file", new File([blob], "property.jpg", { type: "image/jpeg" }));
      await api.post(`/properties/${property.id}/custom-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Custom photo saved");
      onSaved?.({ has_custom_image: true });
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to upload");
    } finally {
      setSaving(false);
    }
  };

  const removeCustomUpload = async () => {
    setSaving(true);
    try {
      await api.delete(`/properties/${property.id}/custom-image`);
      toast.success("Custom photo removed");
      setUploadSrc(null);
      setMode("streetview");
      onSaved?.({ has_custom_image: false });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to remove");
    } finally {
      setSaving(false);
    }
  };

  const saveGoogleSource = async () => {
    setSaving(true);
    try {
      const body = {
        heading: useAuto || mode === "staticmap" ? null : Math.round(heading),
        source: mode === "staticmap" ? "staticmap" : null,
      };
      await api.put(`/properties/${property.id}/image-preference`, body);
      // If the user is switching back from a custom upload, clear it server-side.
      if (property.custom_image_data_uri || property.has_custom_image) {
        try { await api.delete(`/properties/${property.id}/custom-image`); } catch { /* ignore */ }
      }
      toast.success("Photo angle saved");
      onSaved?.({ image_heading: body.heading, image_source: body.source, has_custom_image: false });
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${property.id}/image-preference`, { heading: null, source: null });
      try { await api.delete(`/properties/${property.id}/custom-image`); } catch { /* ignore */ }
      toast.success("Reset to auto-framing");
      setUseAuto(true);
      setMode("streetview");
      onSaved?.({ image_heading: null, image_source: null, has_custom_image: false });
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  const previewHeading = mode === "streetview" && !useAuto ? heading : undefined;
  const previewSource = mode === "staticmap" ? "staticmap" : "streetview";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-xl" data-testid="retake-photo-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-slate-500" />
            Adjust photo — {property.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setMode("streetview")}
              className={`px-2 py-2 rounded-sm border text-xs font-mono uppercase tracking-widest inline-flex items-center justify-center gap-1.5 transition ${mode === "streetview" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
              data-testid="retake-source-streetview"
            >
              <MapPin className="w-3.5 h-3.5" /> Street
            </button>
            <button
              type="button"
              onClick={() => setMode("staticmap")}
              className={`px-2 py-2 rounded-sm border text-xs font-mono uppercase tracking-widest inline-flex items-center justify-center gap-1.5 transition ${mode === "staticmap" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
              data-testid="retake-source-satellite"
            >
              <Satellite className="w-3.5 h-3.5" /> Aerial
            </button>
            <button
              type="button"
              onClick={() => setMode("upload")}
              className={`px-2 py-2 rounded-sm border text-xs font-mono uppercase tracking-widest inline-flex items-center justify-center gap-1.5 transition ${mode === "upload" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
              data-testid="retake-source-upload"
            >
              <Upload className="w-3.5 h-3.5" /> Upload
            </button>
          </div>

          {/* Preview / cropper area */}
          {mode !== "upload" && (
            <div className="w-full h-56 bg-slate-100 rounded-sm overflow-hidden border border-slate-200">
              <PropertyImage
                pid={property.id}
                size="640x320"
                className="w-full h-56"
                rounded="rounded-sm"
                alt={property.name}
                heading={previewHeading}
                source={previewSource}
                refreshKey={`${heading}-${mode}-${useAuto ? "auto" : "manual"}`}
              />
            </div>
          )}

          {mode === "upload" && (
            <>
              {!uploadSrc && (
                <div
                  className="w-full h-56 bg-slate-50 border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center text-slate-500 cursor-pointer hover:border-slate-500 hover:text-slate-700 transition"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-dropzone"
                >
                  <Upload className="w-8 h-8 mb-2" />
                  <div className="text-sm font-medium">Click to choose a photo</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest mt-1 text-slate-400">
                    PNG · JPG · WEBP up to 12 MB
                  </div>
                </div>
              )}
              {uploadSrc && (
                <>
                  <div className="relative w-full h-56 bg-slate-900 rounded-sm overflow-hidden border border-slate-200">
                    <Cropper
                      image={uploadSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={CROP_ASPECT}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      objectFit="contain"
                    />
                  </div>
                  <div>
                    <div className="overline text-slate-500 mb-1">Zoom</div>
                    <input
                      type="range"
                      min="1"
                      max="4"
                      step="0.05"
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="w-full accent-slate-900"
                      data-testid="upload-zoom-slider"
                    />
                    <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-1">
                      <span>1×</span><span>2×</span><span>3×</span><span>4×</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500">
                    Drag the photo to reposition · Pinch / scroll to zoom
                  </div>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onFilePicked}
                className="hidden"
                data-testid="upload-file-input"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-outline-dark text-xs flex-1 inline-flex items-center justify-center gap-1.5"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-choose-btn"
                >
                  <Upload className="w-3.5 h-3.5" /> {uploadSrc ? "Choose different photo" : "Choose photo"}
                </button>
                {(property.has_custom_image || property.custom_image_data_uri) && (
                  <button
                    type="button"
                    className="btn-outline-dark text-xs inline-flex items-center justify-center gap-1.5 text-red-600 hover:text-red-700"
                    onClick={removeCustomUpload}
                    disabled={saving}
                    data-testid="upload-remove-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>
            </>
          )}

          {mode === "streetview" && (
            <>
              <div className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Compass className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-sm text-slate-900 font-medium">
                      {useAuto ? "Auto — face the building" : `Manual heading: ${Math.round(heading)}°`}
                    </div>
                    <div className="text-xs font-mono text-slate-500 uppercase tracking-wider">
                      {useAuto ? "Camera bearing computed from street" : "Override persisted on save"}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="text-xs font-mono uppercase tracking-widest text-slate-600 hover:text-slate-900 underline underline-offset-2"
                  onClick={() => setUseAuto((v) => !v)}
                  data-testid="retake-auto-toggle"
                >
                  {useAuto ? "Set manually" : "Use auto"}
                </button>
              </div>

              <div className={useAuto ? "opacity-60 pointer-events-none" : ""}>
                <input
                  type="range"
                  min="0"
                  max="359"
                  step="1"
                  value={Math.round(heading)}
                  onChange={(e) => { setUseAuto(false); setHeading(Number(e.target.value)); }}
                  className="w-full accent-slate-900"
                  data-testid="retake-heading-slider"
                />
                <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-1">
                  <span>0° N</span><span>90° E</span><span>180° S</span><span>270° W</span><span>359°</span>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <button type="button" className="btn-outline-dark text-xs inline-flex items-center justify-center gap-1" onClick={() => nudge(-15)} data-testid="retake-rotate-left-15">
                  <RotateCcw className="w-3.5 h-3.5" /> −15°
                </button>
                <button type="button" className="btn-outline-dark text-xs inline-flex items-center justify-center gap-1" onClick={() => nudge(-5)} data-testid="retake-rotate-left-5">
                  <RotateCcw className="w-3.5 h-3.5" /> −5°
                </button>
                <button type="button" className="btn-outline-dark text-xs inline-flex items-center justify-center gap-1" onClick={() => nudge(5)} data-testid="retake-rotate-right-5">
                  +5° <RotateCw className="w-3.5 h-3.5" />
                </button>
                <button type="button" className="btn-outline-dark text-xs inline-flex items-center justify-center gap-1" onClick={() => nudge(15)} data-testid="retake-rotate-right-15">
                  +15° <RotateCw className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {COMPASS.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => { setUseAuto(false); setHeading(c.value); }}
                    className={`px-2 py-1.5 rounded-sm border text-xs font-mono uppercase tracking-widest ${!useAuto && Math.round(heading) === c.value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
                    data-testid={`retake-compass-${c.label.toLowerCase()}`}
                  >
                    {c.label} · {c.value}°
                  </button>
                ))}
              </div>
            </>
          )}

          {mode === "staticmap" && (
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
              Aerial satellite view — heading not applicable
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <button
            type="button"
            className="btn-outline-dark text-sm"
            onClick={reset}
            disabled={saving}
            data-testid="retake-reset-btn"
          >
            Reset to auto
          </button>
          <button
            type="button"
            className="btn-outline-dark"
            onClick={onClose}
            disabled={saving}
            data-testid="retake-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-dark"
            onClick={mode === "upload" ? saveCustomUpload : saveGoogleSource}
            disabled={saving || (mode === "upload" && !uploadSrc)}
            data-testid="retake-save-btn"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
